import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { ApiRequestError, api, type CurrentUser } from "./api.js";
import {
  adoptClientInstanceFromOpenPage,
  broadcastSessionLogout,
  clearClientInstanceToken,
  startSessionCoordination,
} from "./session-lifecycle.js";

import {
  advanceSessionEpoch,
  onSessionExpired,
  sessionEpoch,
} from "./session-expiry.js";

interface AuthProblem {
  message: string;
  reference: string | null;
}

interface AuthState {
  loading: boolean;
  user: CurrentUser | null;
  mustChangePassword: boolean;
  error: AuthProblem | null;
  systemError: string | null;
  login(username: string, password: string): Promise<void>;
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [error, setError] = useState<AuthProblem | null>(null);
  const [systemError, setSystemError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const stopExpiry = onSessionExpired(() => {
      if (!active) return;
      clearClientInstanceToken();
      broadcastSessionLogout();
      setUser(null);
      setMustChangePassword(false);
      setError({
        message: "Sessione scaduta. Accedi di nuovo per continuare.",
        reference: null,
      });
    });
    const stopCoordination = startSessionCoordination(() => {
      advanceSessionEpoch();
      if (active) {
        setUser(null);
        setMustChangePassword(false);
      }
    });
    const prepare = async (): Promise<void> => {
      try {
        await adoptClientInstanceFromOpenPage();
        const health = await api.readiness();
        if (health.status !== "ok") {
          if (active)
            setSystemError(
              health.message ?? "Gestionale temporaneamente non pronto.",
            );
          return;
        }
        try {
          const epoch = sessionEpoch();
          const current = await api.currentUser();
          if (active && epoch === sessionEpoch()) {
            const { mustChangePassword: required, ...currentUser } = current;
            setUser(currentUser);
            setMustChangePassword(required);
          }
        } catch {
          // A missing session is expected before login.
        }
      } catch (cause) {
        if (active)
          setSystemError(
            cause instanceof Error
              ? cause.message
              : "Il servizio del gestionale non risponde. Attendere qualche secondo e riprovare.",
          );
      } finally {
        if (active) setLoading(false);
      }
    };
    void prepare();
    return () => {
      active = false;
      stopCoordination();
      stopExpiry();
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      loading,
      user,
      mustChangePassword,
      error,
      systemError,
      login: async (username, password) => {
        const epoch = advanceSessionEpoch();
        setError(null);
        try {
          const result = await api.login(username, password);
          if (epoch !== sessionEpoch()) return;
          setUser(result.user);
          setMustChangePassword(result.mustChangePassword);
        } catch (loginError) {
          if (epoch !== sessionEpoch()) return;
          const requestError =
            loginError instanceof ApiRequestError ? loginError : null;
          const message =
            requestError?.code === "INVALID_CREDENTIALS"
              ? "Nome utente o password non corretti. Controlla maiuscole e tastiera."
              : requestError?.code === "ACCOUNT_LOCKED"
                ? "Troppi tentativi non riusciti. Attendi 15 minuti prima di riprovare."
                : loginError instanceof Error
                  ? loginError.message
                  : "Accesso non riuscito";
          setError({
            message,
            reference: requestError?.correlationId ?? null,
          });
          throw loginError;
        }
      },
      changePassword: async (currentPassword, newPassword) => {
        await api.changePassword(currentPassword, newPassword);
        setMustChangePassword(false);
      },
      logout: async () => {
        advanceSessionEpoch();
        try {
          await api.logout();
        } finally {
          clearClientInstanceToken();
          broadcastSessionLogout();
          setUser(null);
          setMustChangePassword(false);
        }
      },
    }),
    [error, loading, mustChangePassword, systemError, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthState => {
  const value = useContext(AuthContext);
  if (value === null)
    throw new Error("useAuth must be used inside AuthProvider");
  return value;
};
